import StudentProgressCheckup from "./StudentProgressCheckup";
import StudentDashboard from "./StudentDashboard";
import LogTimelineReport from "./LogTimelineReport";
import DietAnalysis from "./DietAnalysis";
// src/pages/StudentKiosk.jsx
// ─────────────────────────────────────────────────────────────────────────────
// Full-screen self-scan kiosk for students.
// Students walk up, face is detected, attendance is marked automatically.
// After marking, prompts them to fill their daily diary.
// ─────────────────────────────────────────────────────────────────────────────

import { useState, useEffect, useRef, useCallback } from "react";
import { useFaceRecognition } from "../hooks/useFaceRecognition";
import { 
  markAttendance, saveDiaryEntry, getTodayDiary, getEnrolledStudents, 
  saveMessageLog 
} from "../firebase/database";
import { useAuth } from "../context/AuthContext";
import { useNavigate } from "react-router-dom";
import { getInitials, sendWhatsAppMessage } from "../utils/helpers";
import { theme } from "../utils/theme";

const C = theme.dark;

const KIOSK_STEPS = { 
  IDLE: "idle", 
  SCANNING: "scanning", 
  DASHBOARD: "dashboard",
  DONE: "done"
};

export default function StudentKiosk() {
  const { role, logout } = useAuth();
  const navigate      = useNavigate();
  const videoRef    = useRef();
  const canvasRef   = useRef();
  const streamRef   = useRef();

  const [step, setStep]         = useState(KIOSK_STEPS.IDLE);
  const [matched, setMatched]   = useState(null);   // { studentId, name, confidence, ... }
  const [isCheckOut, setIsCheckOut] = useState(false);
  const [students, setStudents] = useState([]);
  const [alreadyMarked, setAlreadyMarked] = useState(false);
  const [matchError, setMatchError] = useState(null);
  const [accessGranted, setAccessGranted] = useState(false);

  const { modelsLoaded, loading: modelsLoading, error: modelError,
          loadEnrolledStudents, startRecognitionLoop, stopRecognitionLoop, drawDetections } = useFaceRecognition();

  // Load enrolled students on mount
  useEffect(() => {
    getEnrolledStudents().then(list => {
      setStudents(list);
      loadEnrolledStudents(list);
    });
  }, [loadEnrolledStudents]);

  const startCamera = useCallback(async () => {
    try {
      setMatchError(null);
      const stream = await navigator.mediaDevices.getUserMedia({
        video: { width: 640, height: 480, facingMode: "user" },
      });
      streamRef.current = stream;
      setStep(KIOSK_STEPS.SCANNING);
    } catch (e) {
      console.error("Camera error:", e);
      setMatchError("Could not access camera. Please check permissions.");
    }
  }, []);

  // Attach stream and start detection overlay
  useEffect(() => {
    if (step === KIOSK_STEPS.SCANNING && streamRef.current && videoRef.current) {
      videoRef.current.srcObject = streamRef.current;
      videoRef.current.play().catch(e => console.error("Video play failed:", e));
      
      // Start detection drawing loop
      const drawLoop = setInterval(() => {
        if (videoRef.current && canvasRef.current && videoRef.current.videoWidth > 0) {
          drawDetections(videoRef.current, canvasRef.current);
        }
      }, 100);
      
      return () => clearInterval(drawLoop);
    }
  }, [step, drawDetections]);

  const stopCamera = useCallback(() => {
    stopRecognitionLoop();
    streamRef.current?.getTracks().forEach(t => t.stop());
    streamRef.current = null;
  }, [stopRecognitionLoop]);

  // Start recognition loop when scanning
  useEffect(() => {
    if (step !== KIOSK_STEPS.SCANNING || !modelsLoaded || !videoRef.current) return;

    const stop = startRecognitionLoop(videoRef.current, async (result) => {
      if (result.confidence < 62) return;

      stopRecognitionLoop();
      const student = students.find(s => s.id === result.studentId);
      
      if (!student) {
        setMatchError("Member profile not found.");
        stopCamera();
        setStep(KIOSK_STEPS.IDLE);
        return;
      }

      // Mark attendance
      const res = await markAttendance(result.studentId);
      
      setAlreadyMarked(!!res?.alreadyDone);
      setIsCheckOut(!!res?.isCheckOut);
      setMatched({ ...student, ...result });
      setAccessGranted(true);
      
      // Brief confirmation before dashboard
      setTimeout(() => {
        setAccessGranted(false);
        setStep(KIOSK_STEPS.DASHBOARD);
      }, 2000);

      stopCamera();
    }, 1200);

    return stop;
  }, [step, modelsLoaded, students, startRecognitionLoop, stopRecognitionLoop, stopCamera]);

  // INACTIVITY TIMER: Reset Kiosk if student walks away from Dashboard
  useEffect(() => {
    if (step !== KIOSK_STEPS.DASHBOARD) return;
    
    let timeout;
    const resetTimer = () => {
      clearTimeout(timeout);
      timeout = setTimeout(() => {
        handleLogout();
      }, 60000); // 60 seconds
    };

    resetTimer();

    const events = ["mousemove", "keydown", "click", "touchstart"];
    events.forEach(e => window.addEventListener(e, resetTimer));
    
    return () => {
      clearTimeout(timeout);
      events.forEach(e => window.removeEventListener(e, resetTimer));
    };
  }, [step]);

  const handleLogout = () => {
    setStep(KIOSK_STEPS.DONE);
  };

  // Auto-reset on DONE
  useEffect(() => {
    if (step !== KIOSK_STEPS.DONE) return;
    const t = setTimeout(() => {
      setStep(KIOSK_STEPS.IDLE);
      setMatched(null);
      setIsCheckOut(false);
      setAlreadyMarked(false);
    }, 3000);
    return () => clearTimeout(t);
  }, [step]);

  return (
    <div style={{ minHeight: "100vh", background: C.bg, display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", overflow: "hidden", position: "relative", fontFamily: "'DM Sans', sans-serif" }}>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=DM+Sans:wght@300;400;500;600;700&display=swap');
        @keyframes scanY { 0%,100%{transform:translateY(0)} 50%{transform:translateY(220px)} }
        @keyframes pulse { 0%,100%{opacity:1} 50%{opacity:.3} }
        @keyframes fadeUp { from{opacity:0;transform:translateY(20px)} to{opacity:1;transform:none} }
        @keyframes scaleIn { from{opacity:0;transform:scale(.8)} to{opacity:1;transform:scale(1)} }
        @keyframes rotate { from{transform:rotate(0deg)} to{transform:rotate(360deg)} }
        .fadeUp { animation: fadeUp .4s ease forwards; }
        .scaleIn { animation: scaleIn .35s cubic-bezier(.34,1.56,.64,1) forwards; }
        .pulse { animation: pulse 2s infinite; }
        * { box-sizing: border-box; margin: 0; padding: 0; }
      `}</style>

      {/* Header (Hidden in Dashboard) */}
      {step !== KIOSK_STEPS.DASHBOARD && (
        <div style={{ position: "fixed", top: 0, left: 0, right: 0, padding: "24px 40px", display: "flex", alignItems: "center", justifyContent: "space-between", zIndex: 100 }}>
          <div>
            <div style={{ fontSize: 20, fontWeight: 800, color: C.text, letterSpacing: -0.5 }}>LIFEWELLNESS</div>
            <div style={{ fontSize: 12, color: C.muted, fontWeight: 500, letterSpacing: 1, textTransform: "uppercase" }}>Attendance Kiosk</div>
          </div>
          <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
            <div style={{ width: 8, height: 8, borderRadius: "50%", background: modelsLoaded ? C.green : C.amber, boxShadow: modelsLoaded ? `0 0 10px ${C.green}88` : "none" }} className={modelsLoaded ? "" : "pulse"} />
            <span style={{ fontSize: 12, color: C.muted, fontWeight: 600 }}>{modelsLoading ? "INITIALIZING..." : modelsLoaded ? "SYSTEM READY" : "ERROR"}</span>
          </div>
        </div>
      )}

      {/* ── IDLE ── */}
      {step === KIOSK_STEPS.IDLE && (
        <div className="fadeUp" style={{ textAlign: "center", padding: 20 }}>
          <div style={{ position: "relative", marginBottom: 40 }}>
            <div style={{ width: 140, height: 140, borderRadius: "50%", background: `linear-gradient(135deg, ${C.accent}22, ${C.accent}05)`, border: `1px solid ${C.accent}33`, display: "flex", alignItems: "center", justifyContent: "center", margin: "0 auto" }}>
              <div style={{ fontSize: 60 }}>👤</div>
            </div>
            <div style={{ position: "absolute", top: -10, right: "calc(50% - 70px)", width: 40, height: 40, borderRadius: "50%", background: C.accent, display: "flex", alignItems: "center", justifyContent: "center", boxShadow: "0 4px 12px rgba(61, 126, 255, 0.4)" }}>
              <div style={{ width: 16, height: 16, border: "2px solid #fff", borderTopColor: "transparent", borderRadius: "50%", animation: "rotate 1s linear infinite" }} />
            </div>
          </div>

          <h1 style={{ fontSize: 42, fontWeight: 800, color: C.text, marginBottom: 16, letterSpacing: -1 }}>Welcome Back</h1>
          <p style={{ fontSize: 18, color: C.muted, marginBottom: 48, maxWidth: 400, margin: "0 auto 48px", lineHeight: 1.6 }}>
            Ready for your session? Stand in front of the camera to unlock your dashboard.
          </p>

          {matchError && (
            <div style={{ background: C.red + "15", border: `1px solid ${C.red}33`, color: C.red, padding: "16px 24px", borderRadius: 16, marginBottom: 32, fontSize: 14, fontWeight: 600 }}>
              {matchError}
            </div>
          )}

          <button
            onClick={startCamera}
            disabled={!modelsLoaded}
            style={{ 
              fontSize: 18, 
              fontWeight: 700, 
              padding: "20px 48px", 
              borderRadius: 20, 
              background: modelsLoaded ? C.accent : C.dim, 
              color: "#fff", 
              border: "none", 
              cursor: modelsLoaded ? "pointer" : "not-allowed", 
              boxShadow: modelsLoaded ? "0 10px 25px rgba(61, 126, 255, 0.3)" : "none",
              transition: "all 0.2s ease"
            }}
            onMouseEnter={(e) => { if(modelsLoaded) e.currentTarget.style.transform = "translateY(-2px)"; }}
            onMouseLeave={(e) => { if(modelsLoaded) e.currentTarget.style.transform = "translateY(0)"; }}
          >
            {modelsLoaded ? "Tap to Start Scanning" : "Loading Face Models..."}
          </button>
        </div>
      )}

      {/* ── SCANNING ── */}
      {step === KIOSK_STEPS.SCANNING && (
        <div className="fadeUp" style={{ width: "100%", maxWidth: 600, padding: 20 }}>
          <div style={{ textAlign: "center", marginBottom: 32 }}>
            <h2 style={{ fontSize: 24, fontWeight: 700, color: C.text, marginBottom: 8 }}>Position Your Face</h2>
            <p style={{ fontSize: 14, color: C.muted }}>Ensure good lighting and look directly at the camera</p>
          </div>

          <div style={{ position: "relative", width: "100%", paddingBottom: "75%", borderRadius: 32, overflow: "hidden", background: "#000", border: `4px solid ${C.accent}22`, boxShadow: "0 20px 50px rgba(0,0,0,0.3)" }}>
            <video ref={videoRef} autoPlay muted playsInline style={{ position: "absolute", inset: 0, width: "100%", height: "100%", objectFit: "cover", transform: "scaleX(-1)" }} />
            <canvas ref={canvasRef} style={{ position: "absolute", inset: 0, width: "100%", height: "100%", transform: "scaleX(-1)" }} />
            
            {/* Overlay */}
            <div style={{ position: "absolute", inset: 0, background: `radial-gradient(circle, transparent 40%, rgba(0,0,0,0.4) 100%)` }} />
            
            {/* Scan line */}
            <div style={{ position: "absolute", left: 0, right: 0, height: 3, background: `linear-gradient(90deg, transparent, ${C.accent}, transparent)`, boxShadow: `0 0 20px ${C.accent}`, animation: "scanY 3s ease-in-out infinite", pointerEvents: "none" }} />
            
            {/* Corner brackets */}
            {[["topleft","top left"],["topright","top right"],["bottomleft","bottom left"],["bottomright","bottom right"]].map(([key, pos]) => {
              const [v, h] = pos.split(" ");
              return (
                <div key={key} style={{ position: "absolute", [v]: 32, [h]: 32, width: 48, height: 48,
                  borderTop:    v === "top"    ? `4px solid ${C.accent}` : "none",
                  borderBottom: v === "bottom" ? `4px solid ${C.accent}` : "none",
                  borderLeft:   h === "left"   ? `4px solid ${C.accent}` : "none",
                  borderRight:  h === "right"  ? `4px solid ${C.accent}` : "none",
                  borderRadius: 4
                }} />
              );
            })}
          </div>

          <div style={{ textAlign: "center", marginTop: 40 }}>
            <button 
              onClick={() => { stopCamera(); setStep(KIOSK_STEPS.IDLE); }} 
              style={{ fontSize: 15, fontWeight: 600, color: C.muted, background: "rgba(255,255,255,0.05)", border: `1px solid ${C.border}`, padding: "12px 24px", borderRadius: 12, cursor: "pointer" }}
            >
              Cancel & Go Back
            </button>
          </div>
        </div>
      )}

      {/* ── ACCESS GRANTED OVERLAY ── */}
      {accessGranted && matched && (
        <div style={{ position: "fixed", inset: 0, background: C.bg, zIndex: 1000, display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", animation: "fadeUp 0.3s ease" }}>
          <div style={{ width: 100, height: 100, borderRadius: "50%", background: C.green + "22", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 40, color: C.green, marginBottom: 24, border: `2px solid ${C.green}` }}>
            ✓
          </div>
          <h2 style={{ fontSize: 32, fontWeight: 800, color: C.text, marginBottom: 8 }}>Access Granted</h2>
          <p style={{ fontSize: 18, color: C.muted }}>Welcome, {matched.name}</p>
          <div style={{ marginTop: 32, display: "flex", gap: 8 }}>
            {[0,1,2].map(i => <div key={i} style={{ width: 8, height: 8, borderRadius: "50%", background: C.accent, opacity: 0.3, animation: `pulse 1s infinite ${i * 0.2}s` }} />)}
          </div>
        </div>
      )}

      {/* ── DASHBOARD ── */}
      {step === KIOSK_STEPS.DASHBOARD && matched && (
        <div style={{ position: "fixed", inset: 0, zIndex: 500, background: "#fff" }}>
          <StudentDashboard
            studentId={matched.id}
            onBack={handleLogout}
            fullScreen={true}
          />
        </div>
      )}

      {/* ── DONE ── */}
      {step === KIOSK_STEPS.DONE && (
        <div className="scaleIn" style={{ textAlign: "center" }}>
          <div style={{ fontSize: 80, marginBottom: 24 }}>👋</div>
          <h1 style={{ fontSize: 36, fontWeight: 800, color: C.text, marginBottom: 12 }}>See You Soon!</h1>
          <p style={{ fontSize: 18, color: C.muted }}>Your session has been securely ended.</p>
          <div style={{ marginTop: 40, fontSize: 13, color: C.dim }}>Returning to standby screen...</div>
        </div>
      )}

      {/* Admin/Kiosk Footer (Hidden in Dashboard) */}
      {step !== KIOSK_STEPS.DASHBOARD && (
        <div style={{ position: "fixed", bottom: 0, left: 0, right: 0, padding: "20px 40px", display: "flex", justifyContent: "space-between", alignItems: "center", zIndex: 100 }}>
          <div style={{ display: "flex", gap: 16 }}>
            { (role === "admin" || role === "trainer" || role === "instructor") && (
              <button 
                onClick={() => navigate("/admin")} 
                style={{ background: "rgba(255,255,255,0.05)", border: `1px solid ${C.border}`, color: C.muted, fontSize: 13, fontWeight: 600, padding: "10px 20px", borderRadius: 10, cursor: "pointer" }}
              >
                Admin Panel
              </button>
            )}
            <button 
              onClick={logout} 
              style={{ background: "none", border: "none", color: C.red, fontSize: 12, fontWeight: 600, cursor: "pointer", opacity: 0.6 }}
            >
              Exit Kiosk
            </button>
          </div>
          <div style={{ fontSize: 12, color: C.muted, fontWeight: 500 }}>
            {new Date().toLocaleTimeString("en-IN", { hour: "2-digit", minute: "2-digit" })}
          </div>
        </div>
      )}
    </div>
  );
}
